import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { LenderContractSigningApi } from '../tokens/lender-contract-signing.token';
import { ContractSigningView } from '../models/contract-signing.types';

/**
 * REST-only implementation of `LenderContractSigningApi`.
 *
 * Calls:
 *   GET  /services/embedded/api/business/lender/contract-signing/{applicationId}
 *   POST /services/embedded/api/business/lender/contract-signing/{applicationId}/upload
 *
 * Rule 07 / Rule 10 — no mock fallback. Playwright specs intercept via `page.route()`.
 * Authority: ROLE_LENDER or ROLE_LENDER_RM or ROLE_MASTER_ADMIN (enforced server-side + roleGuard).
 */
@Injectable()
export class LenderContractSigningRestService implements LenderContractSigningApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.EMBEDDED_API_BASE}/business/lender/contract-signing`;

  getView(applicationId: number): Observable<ContractSigningView> {
    return this.http.get<ContractSigningView>(`${this.base}/${applicationId}`);
  }

  downloadContract(applicationId: number): Observable<HttpResponse<Blob>> {
    // Streamed through HttpClient (not window.open) so the auth interceptor attaches the bearer
    // token — the endpoint is authenticated and party-scoped server-side.
    return this.http.get(`${this.base}/${applicationId}/document`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  uploadSignedContract(applicationId: number, file: File): Observable<ContractSigningView> {
    const form = new FormData();
    form.append('file', file);
    // Do NOT set Content-Type manually — the browser sets multipart boundary automatically.
    return this.http.post<ContractSigningView>(`${this.base}/${applicationId}/upload`, form);
  }
}
